import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Popover from '@material-ui/core/Popover';
import Popper from '@material-ui/core/Popper';
import { useTheme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import { ANYTIME_PREFERENCE_VALUE } from 'constants/advancedSearch';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import FPIcon from 'UI/components/atoms/FPIcon';
import SvgCheck from 'UI/res/icons/filled/Check';
import { formatNumber } from 'UI/utils';

import { DEFAULT_PROPS, POPOVER_CONFIG, priorities } from './AdvancedSearch.constants';
import {
  useEditPopover,
  useOptionsManager,
  usePriorityPreferenceManager,
  useSelectionManager
} from './AdvancedSearch.hooks';
import { useAdvancedSearchStyles } from './AdvancedSearch.styled';
import {
  checkIfItemSelected,
  extractOptionId,
  extractOptionLabel,
  getPopupIndicator,
  sortOptionsByGroup
} from './AdvancedSearch.utils';
import { PreferenceSelector, PrioritySelector, SelectedItemChip } from './components';

const COPIES = strings.shared.advancedSearch;

const AdvancedSearchComponent = ({
  apiClient = null,
  defaultPreference = DEFAULT_PROPS.defaultPreference,
  defaultPriority = DEFAULT_PROPS.defaultPriority,
  endpoint = null,
  fetchOptions = null,
  fieldName,
  getOptionId = extractOptionId,
  getOptionLabel = extractOptionLabel,
  groupBy = null,
  noOptionsText = COPIES.noOptionsText,
  onSelectionChange = () => {},
  options = [],
  placeholder,
  preferences = [],
  preferencesApiClient = null,
  preferencesEndpoint = null,
  renderOption = null,
  selectedItems: initialSelectedItems = [],
  isSingleSelect = false,
  title,
  typeahead = false,
  typeaheadLimit = 25,
  typeaheadParams = {}
}) => {
  const theme = useTheme();
  const classes = useAdvancedSearchStyles();
  const inputPlaceholder = placeholder ?? COPIES.placeholder;
  const collapsedLabel =
    placeholder || strings.formatString(COPIES.addFilter, { title: title || inputPlaceholder });
  const [selectedItems, setSelectedItems] = useState(initialSelectedItems);
  const fieldRef = useRef(null);
  const shouldShowPreferences =
    Boolean(preferencesEndpoint) || (Array.isArray(preferences) && preferences.length > 0);

  const memoizedFetchOptions = useCallback(fetchOptions, [fetchOptions]);

  useEffect(() => {
    setSelectedItems(initialSelectedItems);
  }, [initialSelectedItems]);

  const { selectedPriority, setSelectedPriority, selectedPreference, setSelectedPreference } =
    usePriorityPreferenceManager({
      defaultPriority,
      defaultPreference: shouldShowPreferences ? defaultPreference : null
    });

  const handleOnSelectionChange = useCallback(
    newItems => {
      setSelectedItems(newItems);
      onSelectionChange(fieldRef.current, newItems);
    },
    [onSelectionChange]
  );

  const {
    containerRef,
    handleItemAdd,
    handleItemRemove,
    handleOnAdd,
    handlePreferenceChange: handleSelectionPreferenceChange,
    handlePriorityChange: handleSelectionPriorityChange,
    inputValue,
    isInputVisible,
    searchInputRef,
    setInputValue
  } = useSelectionManager({
    getOptionId,
    isSingleSelect,
    onSelectionChange: handleOnSelectionChange,
    selectedItems,
    selectedPreference,
    selectedPriority
  });

  const setField = useCallback(
    incomingFieldName => {
      fieldRef.current = fieldName ?? incomingFieldName;
    },
    [fieldName]
  );

  const AutocompletePopper = useCallback(
    props => (
      <Popper
        {...props}
        placement="bottom-start"
        style={{ ...props.style, width: containerRef.current?.offsetWidth ?? props.style?.width }}
      />
    ),
    [containerRef]
  );

  const renderDefaultOption = useCallback(
    option =>
      typeof option?.count === 'number' ? (
        <Box className={classes.priorityMenuItem}>
          <span>{getOptionLabel(option)}</span>
          <Typography variant="body2" color="textSecondary">
            {formatNumber(option.count)}
          </Typography>
        </Box>
      ) : (
        getOptionLabel(option)
      ),
    [classes.priorityMenuItem, getOptionLabel]
  );

  const renderOptionRow = useCallback(
    option => {
      const isSelected = checkIfItemSelected({ selectedItems, newItem: option, getOptionId });
      return (
        <Box className={clsx(classes.optionRow, isSelected && classes.optionSelected)}>
          {(renderOption || renderDefaultOption)(option)}
        </Box>
      );
    },
    [
      renderOption,
      renderDefaultOption,
      selectedItems,
      getOptionId,
      classes.optionRow,
      classes.optionSelected
    ]
  );

  const { availableOptions, loading } = useOptionsManager({
    apiClient,
    endpoint,
    options,
    fetchOptions: memoizedFetchOptions,
    typeahead,
    typeaheadLimit,
    typeaheadParams,
    inputValue,
    onGetField: setField
  });

  const autocompleteOptions = useMemo(
    () => sortOptionsByGroup(availableOptions, groupBy, getOptionLabel),
    [availableOptions, groupBy, getOptionLabel]
  );

  const { availableOptions: availablePreferenceOptions } = useOptionsManager({
    apiClient: preferencesApiClient ?? apiClient,
    endpoint: preferencesEndpoint,
    fetchOptions: memoizedFetchOptions,
    inputValue: '',
    onGetField: null,
    options: shouldShowPreferences ? preferences : null,
    typeahead: false,
    typeaheadLimit,
    typeaheadParams: {}
  });

  const resolvedPreferences = shouldShowPreferences
    ? (Array.isArray(availablePreferenceOptions) ? availablePreferenceOptions : []).map(
        preference => ({
          ...preference,
          value: (() => {
            if (!('period' in preference)) return preference.value;
            return preference.period === null ? ANYTIME_PREFERENCE_VALUE : preference.period;
          })(),
          label: preference?.title
        })
      )
    : [];

  const {
    popoverAnchor,
    editingItemId,
    handleEditClick,
    handlePopoverClose,
    handlePriorityChange,
    handlePreferenceChange,
    getPreferenceLabel,
    getPriorityLabel,
    getEditingItem
  } = useEditPopover({
    selectedItems,
    priorities,
    onPriorityChange: handleSelectionPriorityChange,
    onPreferenceChange: handleSelectionPreferenceChange
  });

  return (
    <Box ref={containerRef}>
      <Box className={classes.topWrapper}>
        <When condition={Boolean(title)}>
          <Typography variant="subtitle2">{title}</Typography>
        </When>
        <When condition={selectedItems.length > 0}>
          <FPActionButton onClick={() => handleOnSelectionChange([])} size="small" variant="text">
            {strings.shared.ui.clearAll}
          </FPActionButton>
        </When>
      </Box>

      <When condition={isInputVisible}>
        <Box className={classes.inputContainer}>
          <Autocomplete
            ref={searchInputRef}
            className={classes.searchInput}
            options={autocompleteOptions}
            getOptionLabel={getOptionLabel}
            value={null}
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'reset' && !isSingleSelect) return;
              setInputValue(newInputValue);
            }}
            onChange={handleItemAdd}
            loading={loading}
            groupBy={groupBy}
            disableCloseOnSelect={!isSingleSelect}
            PopperComponent={AutocompletePopper}
            renderOption={renderOptionRow}
            filterOptions={(optionsList, params) => {
              if (typeahead) {
                return optionsList;
              }
              return optionsList.filter(option =>
                getOptionLabel(option).toLowerCase().includes(params.inputValue.toLowerCase())
              );
            }}
            renderInput={params => (
              <TextField
                {...params}
                placeholder={inputPlaceholder}
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {React.cloneElement(params.InputProps.endAdornment, {
                        children: [
                          <When key="clear" condition={inputValue.length > 0}>
                            <IconButton
                              aria-label={strings.shared.ui.clear}
                              size="small"
                              onClick={() => setInputValue('')}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </When>,
                          getPopupIndicator(params.InputProps.endAdornment)
                        ]
                      })}
                    </>
                  )
                }}
              />
            )}
            noOptionsText={noOptionsText}
          />

          <When condition={!shouldShowPreferences}>
            <PrioritySelector
              selectedPriority={selectedPriority}
              onPriorityChange={setSelectedPriority}
              priorities={priorities}
              size="small"
            />
          </When>
        </Box>
      </When>
      <When condition={!isInputVisible}>
        <button
          type="button"
          className={classes.collapsedContainer}
          onClick={handleOnAdd}
          aria-label={collapsedLabel}
        >
          <span>+</span>
          <span>{collapsedLabel}</span>
        </button>
      </When>

      {shouldShowPreferences && isInputVisible && (
        <Box className={classes.selectorsContainer}>
          <PrioritySelector
            selectedPriority={selectedPriority}
            onPriorityChange={setSelectedPriority}
            priorities={priorities}
            size="small"
          />

          <PreferenceSelector
            selectedPreference={selectedPreference}
            onPreferenceChange={setSelectedPreference}
            preferences={resolvedPreferences}
            size="small"
          />
        </Box>
      )}

      <When condition={selectedItems.length > 0}>
        <Box className={classes.selectedItems}>
          {selectedItems.map(selectedItem => (
            <SelectedItemChip
              key={selectedItem.id}
              selectedItem={selectedItem}
              onEdit={handleEditClick}
              onRemove={handleItemRemove}
              getPreferenceLabel={getPreferenceLabel}
              getPriorityLabel={getPriorityLabel}
              getOptionLabel={getOptionLabel}
              isEditing={editingItemId === selectedItem.id}
            />
          ))}
        </Box>
      </When>
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={POPOVER_CONFIG.anchorOrigin}
        transformOrigin={POPOVER_CONFIG.transformOrigin}
      >
        <Paper className={classes.popoverContent}>
          <Typography variant="subtitle2" className={classes.popoverSectionTitle}>
            {COPIES.priority}
          </Typography>
          {priorities.map(priority => {
            const editingItem = getEditingItem();
            return (
              <MenuItem
                key={priority.id}
                onClick={() => handlePriorityChange(editingItemId, priority.id)}
                selected={priority.id === editingItem?.priority}
              >
                <Box className={classes.priorityMenuItem}>
                  <span>{priority.label}</span>
                  {priority.id === editingItem?.priority && (
                    <FPIcon component={SvgCheck} size="sm" color={priority.color} />
                  )}
                </Box>
              </MenuItem>
            );
          })}

          {shouldShowPreferences && (
            <>
              <Typography variant="subtitle2" className={classes.popoverSectionTitle}>
                {COPIES.preferences}
              </Typography>
              {resolvedPreferences.map(preference => {
                const editingItem = getEditingItem();
                return (
                  <MenuItem
                    key={preference.id}
                    onClick={() => handlePreferenceChange(editingItemId, preference.value)}
                    selected={preference.value === editingItem?.preference}
                  >
                    <Box className={classes.priorityMenuItem}>
                      <span>{preference.label}</span>
                      {preference.value === editingItem?.preference && (
                        <FPIcon component={SvgCheck} size="sm" color={theme.palette.success.main} />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </>
          )}
        </Paper>
      </Popover>
    </Box>
  );
};

export const AdvancedSearch = React.memo(AdvancedSearchComponent);
